export function lifeOSClassNames(...names) {
  return names.filter(Boolean).join(' ');
}

const TYPE_LABELS = {
  CALENDAR_EVENT: 'Compromisso',
  TASK: 'Tarefa',
  REMINDER: 'Lembrete',
  ROUTINE_BLOCK: 'Rotina',
  OPEN_LOOP: 'Pendência',
  PROJECT_DEADLINE: 'Prazo de projeto',
  NOTE: 'Nota',
  IDEA: 'Ideia',
  LINK: 'Link',
  FILE: 'Arquivo',
  IMAGE: 'Imagem',
  RECIPE: 'Receita',
  SAVED_ITEM: 'Item salvo',
  PROJECT_RESOURCE: 'Recurso de projeto',
};

export function lifeOSTypeLabel(type) {
  const key = String(type || '').trim().toUpperCase();
  if (TYPE_LABELS[key]) return TYPE_LABELS[key];
  return key
    ? key.toLowerCase().replace(/[_-]+/g, ' ').replace(/\b\w/g, char => char.toUpperCase())
    : '';
}

export function lifeOSItemTitle(item) {
  return String(item?.title || item?.name || item?.body || '').trim();
}

export function lifeOSItemExcerpt(item, maxLength = 180) {
  const raw = String(item?.excerpt || item?.summary || item?.description || item?.content || '').trim();
  if (!raw) return '';
  return raw.length > maxLength ? `${raw.slice(0, maxLength - 1).trimEnd()}…` : raw;
}

function dateFromValue(value) {
  if (!value || typeof value !== 'string') return null;
  // HH:mm values are intentionally kept as wall-clock strings.
  if (/^\d{1,2}:\d{2}(?::\d{2})?$/.test(value.trim())) return null;
  const parsed = Date.parse(value);
  return Number.isFinite(parsed) ? new Date(parsed) : null;
}

export function lifeOSTime(value, timezone) {
  if (!value) return '';
  const raw = String(value).trim();
  const wallClock = raw.match(/^(\d{1,2}):(\d{2})(?::\d{2})?$/);
  if (wallClock) return `${wallClock[1].padStart(2, '0')}:${wallClock[2]}`;
  const parsed = dateFromValue(raw);
  if (!parsed) return raw;
  try {
    return new Intl.DateTimeFormat('pt-BR', {
      timeZone: timezone || undefined,
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    }).format(parsed);
  } catch {
    return parsed.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: false });
  }
}

export function lifeOSDateTime(value, timezone) {
  const parsed = dateFromValue(value);
  if (!parsed) return '';
  try {
    return new Intl.DateTimeFormat('pt-BR', {
      timeZone: timezone || undefined,
      day: '2-digit',
      month: 'short',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    }).format(parsed);
  } catch {
    return parsed.toLocaleString('pt-BR');
  }
}

export function lifeOSRefreshTime(value, timezone) {
  if (!value) return '';
  const parsed = dateFromValue(value);
  if (!parsed) return '';
  try {
    return new Intl.DateTimeFormat('pt-BR', {
      timeZone: timezone || undefined,
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    }).format(parsed);
  } catch {
    return parsed.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: false });
  }
}

function localDay(date, timezone) {
  try {
    const parts = new Intl.DateTimeFormat('en-US', {
      timeZone: timezone || undefined,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    }).formatToParts(date);
    const fields = Object.fromEntries(parts.map(part => [part.type, part.value]));
    return `${fields.year}-${fields.month}-${fields.day}`;
  } catch {
    return date.toISOString().slice(0, 10);
  }
}

export function lifeOSItemIsPast(item, date, timezone, now = new Date()) {
  if (item?.past === true) return true;
  if (['past', 'overdue', 'completed', 'done', 'cancelled', 'canceled', 'archived', 'discarded', 'skipped'].includes(String(item?.status || '').toLowerCase())) return true;
  const point = dateFromValue(item?.time || item?.start_at || item?.due_at || '');
  if (point) return point.getTime() < now.getTime();
  const wallClock = String(item?.time || item?.start_time || '').match(/^(\d{1,2}):(\d{2})/);
  if (!wallClock || !date || date !== localDay(now, timezone)) return false;
  const minutes = Number(wallClock[1]) * 60 + Number(wallClock[2]);
  const currentMinutes = Number(lifeOSTime(now.toISOString(), timezone).slice(0, 2)) * 60
    + Number(lifeOSTime(now.toISOString(), timezone).slice(3, 5));
  return Number.isFinite(currentMinutes) && minutes < currentMinutes;
}

export function lifeOSTimeline(response) {
  if (Array.isArray(response?.timeline)) return response.timeline;
  if (Array.isArray(response?.items)) return response.items;
  return [];
}

export function lifeOSNextItem(response, timeline = lifeOSTimeline(response)) {
  if (response?.next_item) return response.next_item;
  return timeline.find(item => item?.time || item?.start_at || item?.due_at) || null;
}

export function lifeOSStatusLabel(status) {
  const value = String(status || '').trim().toLowerCase();
  const labels = {
    pending: 'Pendente',
    open: 'Em aberto',
    active: 'Ativo',
    in_progress: 'Em andamento',
    completed: 'Concluído',
    done: 'Concluído',
    skipped: 'Pulado',
    archived: 'Arquivado',
    discarded: 'Descartado',
  };
  return labels[value] || (value ? value.replace(/[_-]+/g, ' ') : '');
}
